/** `walls`: per scene. `side`: a coordinate's bound — the server also refuses an end outside the scene's map. */
export const WALL_LIMITS = { walls: 2000, side: 100_000 };
export const WALLS_FULL = 'This map has as many walls as it can hold. Remove some first.';
export const WALL_OFF_MAP = 'A wall must lie on the map.';
export const WALL_KINDS = ['wall.add', 'wall.set', 'wall.remove', 'door.set'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isCoord = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= WALL_LIMITS.side;
const isBool = (v) => typeof v === 'boolean';
function parsePoint(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { x, y } = v;
    return isCoord(x) && isCoord(y) ? { x: Math.round(x * 2) / 2, y: Math.round(y * 2) / 2 } : null;
}
function parseDoor(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    return isBool(v.open) ? { open: v.open } : null;
}
/** Strict: a uuid, two finite ends in bounds and apart, both switches, a door only as `{ open }`. */
export function parseWall(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const w = v;
    if (!isUuid(w.id))
        return null;
    const a = parsePoint(w.a);
    const b = parsePoint(w.b);
    if (!a || !b || (a.x === b.x && a.y === b.y))
        return null;
    if (!isBool(w.move) || !isBool(w.sight))
        return null;
    if (w.floor !== undefined && !(typeof w.floor === 'string' && /^[a-z0-9-]{1,40}$/.test(w.floor)))
        return null;
    const out = { id: w.id, a, b, move: w.move, sight: w.sight, ...(typeof w.floor === 'string' ? { floor: w.floor } : {}) };
    if (w.door === undefined)
        return out;
    const door = parseDoor(w.door);
    return door ? { ...out, door } : null;
}
export function parseWallBody(b) {
    switch (b.kind) {
        case 'wall.add': {
            if (!Array.isArray(b.walls) || b.walls.length < 1 || b.walls.length > WALL_LIMITS.walls)
                return null;
            const walls = b.walls.map(parseWall);
            if (!walls.every((w) => w !== null))
                return null;
            return new Set(walls.map((w) => w.id)).size === walls.length ? { kind: 'wall.add', walls } : null;
        }
        case 'wall.set': {
            if (!isUuid(b.id))
                return null;
            const out = { kind: 'wall.set', id: b.id };
            if (b.move !== undefined) {
                if (!isBool(b.move))
                    return null;
                out.move = b.move;
            }
            if (b.sight !== undefined) {
                if (!isBool(b.sight))
                    return null;
                out.sight = b.sight;
            }
            if (b.door !== undefined) {
                if (b.door === null)
                    out.door = null;
                else {
                    const door = parseDoor(b.door);
                    if (!door)
                        return null;
                    out.door = door;
                }
            }
            return out.move === undefined && out.sight === undefined && out.door === undefined ? null : out;
        }
        case 'wall.remove':
            if (!Array.isArray(b.ids) || b.ids.length < 1 || b.ids.length > WALL_LIMITS.walls || !b.ids.every(isUuid))
                return null;
            return { kind: 'wall.remove', ids: [...new Set(b.ids)] };
        case 'door.set':
            return isUuid(b.id) && isBool(b.open) ? { kind: 'door.set', id: b.id, open: b.open } : null;
        default: return null;
    }
}
const copyWall = (w) => ({ ...w, a: { ...w.a }, b: { ...w.b }, ...(w.door ? { door: { ...w.door } } : {}) });
export const copyWalls = (walls) => walls.map(copyWall);
/** A snapshot's walls, each read as the wire's; anything unreadable is dropped. */
export function normaliseWalls(v) {
    if (!Array.isArray(v))
        return [];
    return v.map(parseWall).filter((w) => w !== null);
}
export function reduceWalls(walls, body) {
    switch (body.kind) {
        case 'wall.add': {
            const have = new Set(walls.map((w) => w.id));
            const fresh = body.walls.filter((w) => !have.has(w.id));
            return fresh.length ? [...walls, ...fresh.map(copyWall)] : walls;
        }
        case 'wall.set':
            return walls.map((w) => {
                if (w.id !== body.id)
                    return w;
                const next = { ...w, ...(body.move !== undefined ? { move: body.move } : {}), ...(body.sight !== undefined ? { sight: body.sight } : {}) };
                if (body.door === null)
                    delete next.door;
                else if (body.door)
                    next.door = { ...body.door };
                return next;
            });
        case 'wall.remove': {
            const gone = new Set(body.ids);
            return walls.some((w) => gone.has(w.id)) ? walls.filter((w) => !gone.has(w.id)) : walls;
        }
        case 'door.set': return walls.map((w) => (w.id === body.id && w.door ? { ...w, door: { open: body.open } } : w));
        default: return walls;
    }
}
/**
 * Why the DM's wall event is refused (null: it stands). The role is the caller's (`forbidden` for anyone else);
 * `extent` is the scene's map, when known: a wall with an end off it is refused.
 */
export function wallRefusal(walls, body, extent) {
    switch (body.kind) {
        case 'wall.add':
            if (body.walls.some((w) => walls.some((x) => x.id === w.id)))
                return 'That id is taken.';
            if (walls.length + body.walls.length > WALL_LIMITS.walls)
                return WALLS_FULL;
            if (extent && body.walls.some((w) => [w.a, w.b].some((p) => p.x > extent.width || p.y > extent.height)))
                return WALL_OFF_MAP;
            return null;
        case 'wall.set': return walls.some((w) => w.id === body.id) ? null : 'That wall is no longer on the map.';
        case 'wall.remove': return body.ids.some((id) => walls.some((w) => w.id === id)) ? null : 'Those walls are no longer on the map.';
        case 'door.set': {
            const w = walls.find((x) => x.id === body.id);
            if (!w)
                return 'That wall is no longer on the map.';
            return w.door ? null : 'That wall is not a door.';
        }
        default: return null;
    }
}
//# sourceMappingURL=walls.js.map